<template>
  <div class="row reverse-mobile justify-content-center">
    <UserMenu></UserMenu>

    <div class="col-12 col-md-9 center-content">
      <CommunityCard class="p-4">
        <div class="d-flex mb-4">
          <FolderHeading />
          <div class="ml-auto">
            <button
              class="btn btn-primary"
              type="button"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <img
                src="../assets/add.svg"
                class="mr-2"
                alt="add scenario icon"
              />
              Add Scenario
            </button>
            <div class="dropdown-menu">
              <router-link :to="uploadRoute" class="dropdown-item">
                Upload Scenario
              </router-link>
              <a v-if="!isOnNative" :href="webEditorUrl" class="dropdown-item">
                Create Scenario
              </a>
              <div class="dropdown-divider"></div>
              <button type="button" class="dropdown-item" @click="createFolder">
                New Folder
              </button>
            </div>
          </div>
        </div>

        <div v-if="isLoading">
          <PleaseWait :bgColour="'#FFFFFF'"></PleaseWait>
        </div>

        <div v-if="scenariosCount <= 0 && !isLoading">
          <EmptyFolder />
        </div>

        <div v-else>
          <div
            v-if="scenarioObjects.length > 0"
            :class="{
              'selection-bar d-flex align-items-center mb-3': true,
              active: selectedUuids.length > 0,
            }"
          >
            <label class="d-flex align-items-center mb-0 mr-3">
              <input
                type="checkbox"
                class="mr-2"
                :checked="allSelected"
                :indeterminate.prop="someSelected"
                @change="toggleSelectAll"
              />
              {{
                selectedUuids.length > 0
                  ? selectedUuids.length + " selected"
                  : "Select all on this page"
              }}
            </label>
            <template v-if="selectedUuids.length > 0">
              <button
                type="button"
                class="btn btn-sm btn-primary mr-2"
                @click="moveSelected"
              >
                Move to…
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light"
                @click="selectedUuids = []"
              >
                Clear
              </button>
            </template>
          </div>

          <div class="placeholder">
            <div
              v-cloak
              v-bind:key="scenario.id"
              v-for="scenario in scenarioObjects"
              class="d-flex align-items-start"
            >
              <input
                type="checkbox"
                class="scenario-select mr-3"
                :aria-label="'Select ' + scenario.name"
                :value="scenario.uuid_id"
                v-model="selectedUuids"
              />
              <Scenario
                class="flex-grow-1 min-width-0"
                :scenario="scenario"
                :currentlySelectedTags="scenarioTags"
                :is-my-scenario="true"
              ></Scenario>
            </div>
          </div>

          <div class="row d-flex justify-content-center">
            <div
              v-if="scenariosCount > 0"
              class="pagination-wrapper col-12 d-flex justify-content-end mt-3 mr-5"
            >
              <template>
                <paginate
                  v-model="page"
                  :page-count="pageCount"
                  :page-range="3"
                  :margin-pages="2"
                  :click-handler="clickCallback"
                  :prev-text="'Prev'"
                  :next-text="'Next'"
                  :container-class="'pagination'"
                  :page-class="'page-item'"
                ></paginate>
              </template>
            </div>
          </div>
        </div>
      </CommunityCard>

      <Footer />
    </div>

    <MoveToFolderModal v-if="moveTarget" />
  </div>
</template>

<script>
import ScenService from "../services/scenarios.service";
import Paginate from "../components/Paginate";
import Scenario from "../components/Scenario";
import UserMenu from "../components/UserMenu";
import ScenarioObject from "../objects/ScenarioObject";
import EmptyFolder from "../components/EmptyFolder";
import FolderHeading from "../components/ScenarioDirectoryTree/FolderHeading";
import PleaseWait from "../components/PleaseWait";
import CommunityCard from "../components/CommunityCard";
import Footer from "../components/Footer";
import MoveToFolderModal from "../components/MoveToFolderModal";
import { createFolder, moveToFolder } from "@/utils/folder.helper";

export default {
  name: "MyScenarios",
  components: {
    Scenario,
    UserMenu,
    Paginate,
    EmptyFolder,
    FolderHeading,
    PleaseWait,
    CommunityCard,
    Footer,
    MoveToFolderModal,
  },
  data() {
    return {
      isLoading: true,
      NUMBER_TO_GET: 5,
      pageCount: 0,
      page: 0,
      from: 0,
      to: 0,
      scenarios: [],
      scenarioTags: [],
      scenariosCount: 0,
      pageNum: 0,
      scenarioObjects: [],
      isActive: false,
      sortByFields: [
        { id: "dateAsc", name: "Created Date ▼", isSelected: false },
        { id: "dateDesc", name: "Created Date ▲", isSelected: true },
        { id: "nameAsc", name: "Name ▼", isSelected: false },
        { id: "nameDesc", name: "Name ▲", isSelected: false },
      ],
      currentSortByName: "Created Date ▲",
      webEditorUrl: process.env.VUE_APP_CONTROL_DOMAIN,
      // uuid_ids of the scenarios ticked on the current page
      selectedUuids: [],
    };
  },
  beforeMount() {
    this.isLoading = true;
    this.getScenarios();
  },
  computed: {
    isOnNative() {
      return this.$store.getters.getIsOnNative;
    },
    moveTarget() {
      return this.$store.getters["scenarioDirectoriesStore/moveTarget"];
    },
    contentsVersion() {
      return this.$store.getters["scenarioDirectoriesStore/contentsVersion"];
    },
    directoryUuid() {
      return this.$route.params.directoryUuid || null;
    },
    // Upload into the folder being viewed
    uploadRoute() {
      return this.directoryUuid
        ? {
            name: "scenarios-upload",
            query: { directoryUuid: this.directoryUuid },
          }
        : { name: "scenarios-upload" };
    },
    allSelected() {
      return (
        this.scenarioObjects.length > 0 &&
        this.selectedUuids.length === this.scenarioObjects.length
      );
    },
    someSelected() {
      return this.selectedUuids.length > 0 && !this.allSelected;
    },
  },
  watch: {
    "$route.params.directoryUuid"() {
      this.page = 1;
      this.getScenarios();
    },
    // A scenario was moved or a folder deleted, so reload the current page
    contentsVersion() {
      this.refreshCurrentPage();
    },
  },
  methods: {
    sortBy(sorter) {
      this.currentSortByName = sorter.name;
      this.sortByFields.forEach((x) => (x.isSelected = false));
      sorter.isSelected = true;

      // page zero and one are the same
      let startAtPage = this.page < 2 ? 0 : this.page - 1;

      this.getScenarios(this.NUMBER_TO_GET * startAtPage);
    },

    // Every fetch uses the selected sorter so the order is the same on every page and refresh
    getScenarios(startAt = 0) {
      const currentSorter = this.sortByFields.find((x) => x.isSelected);
      const sortDirection = currentSorter.id.includes("Asc") ? "ASC" : "DESC";
      const sortBy = currentSorter.id.includes("date") ? "contributedAt" : "name";

      this.isLoading = true;
      this.isActive = true;
      ScenService.myScenarios(
        startAt,
        this.NUMBER_TO_GET,
        sortBy,
        sortDirection,
        this.$route.params.directoryUuid
      ).then((data) => {
        // Selection only covers the scenarios on screen
        this.selectedUuids = [];
        this.scenariosCount = data.total;
        this.scenarioObjects = [];
        for (let scenario in data.scenarios) {
          this.scenarioObjects.push(
            new ScenarioObject(data.scenarios[scenario])
          );
        }
        this.from = parseInt(data.from);
        this.to = parseInt(data.to);
        this.updatePageCount();
        this.isLoading = false;

        // e.g. the only scenario on the last page was moved away
        if (
          this.scenarioObjects.length === 0 &&
          this.page > this.pageCount &&
          this.pageCount > 0
        ) {
          this.fetchPage(this.pageCount);
        }
      });

      this.isActive = false;
    },
    clickCallback(pageNum) {
      this.isActive = true;

      window.scrollTo(0, 0);
      this.fetchPage(pageNum);
    },
    fetchPage(pageNum) {
      this.page = pageNum;
      this.scenarioObjects = [];

      this.getScenarios(this.NUMBER_TO_GET * (pageNum - 1));
    },
    updateFavouriteScenariosList() {
      this.$parent.updateFavouriteScenariosList();
    },
    refreshCurrentPage() {
      this.fetchPage(Math.max(1, this.page));
    },
    createFolder() {
      createFolder(this, this.directoryUuid);
    },
    toggleSelectAll() {
      this.selectedUuids = this.allSelected
        ? []
        : this.scenarioObjects.map((s) => s.uuid_id);
    },
    moveSelected() {
      const count = this.selectedUuids.length;
      const first = this.scenarioObjects.find(
        (s) => s.uuid_id === this.selectedUuids[0]
      );

      moveToFolder(this, {
        type: "scenarios",
        uuids: [...this.selectedUuids],
        name: count === 1 && first ? first.name : `${count} scenarios`,
        currentDirectoryUuid: this.directoryUuid,
      });
    },
    updatePageCount() {
      this.pageCount = Math.ceil(this.scenariosCount / this.NUMBER_TO_GET);
    },
  },
};
</script>

<style scoped>
.selection-bar {
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  background: #ffffff;
}

.selection-bar.active {
  background: var(--isim-primary-light);
}

.scenario-select {
  margin-top: 1.5rem;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.min-width-0 {
  min-width: 0;
}

.placeholder {
  min-height: 300px;
  z-index: 9;
  margin-bottom: 30px;
}

/* Medium Devices, Desktops */
@media only screen and (max-width: 992px) {
  .pagination-wrapper {
    margin: 0 !important;
    padding: 0 !important;
    display: flex;
  }
  .pagination-wrapper ul {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>
